import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  InputAdornment,
  TextField,
  useTheme,
} from "@mui/material";
import { MagnifyingGlass, X } from "@phosphor-icons/react";
import { useDarkMode } from "../../../hooks/useDarkMode";
import { ExtendedSidebar, useExtendedSidebarContext } from "./ExtendedSidebar";
import { IconButton } from "./IconButton";

export const SearchAction: React.FC = () => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const { setIsSearchOpen, setSearchTerm } = useExtendedSidebarContext();
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setIsSearchOpen(isOpen);
    if (isOpen) {
      inputRef.current?.focus();
    }
    return () => {
      setIsSearchOpen(false);
    };
  }, [isOpen, setIsSearchOpen]);

  useEffect(() => {
    setSearchTerm(value);
  }, [value, setSearchTerm]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setValue("");
    setSearchTerm("");
    setIsOpen(false);
  };

  if (!isOpen) {
    return (
      <Box className="extended-sidebar-search">
        <ExtendedSidebar.ActionButton
          icon={MagnifyingGlass}
          onClick={handleOpen}
        />
      </Box>
    );
  }

  const searchIconColor = isDarkMode ? palette.gray.A100 : palette.gray[600];

  return (
    <>
      <Box
        className="extended-sidebar-search"
        sx={{
          display: "flex",
          alignItems: "center",
          flex: 1,
          gap: "8px",
          position: "relative",
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          size="small"
          placeholder="Search agents, interfaces, chats"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlass size={16} color={searchIconColor} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            flex: 1,
            backgroundColor: "transparent",
            "& .MuiOutlinedInput-root": {
              height: "32px",
              borderRadius: "4px",
              backgroundColor: isDarkMode ? palette.gray[900] : "#FFFFFF",
              color: isDarkMode ? palette.gray.A100 : palette.gray[600],
              display: "flex",
              alignItems: "center",
              "& fieldset": {
                borderColor: isDarkMode
                  ? palette.gray[700]
                  : palette.neutral[200],
              },
              "&:hover fieldset": {
                borderColor: isDarkMode ? palette.gray[600] : palette.gray[400],
              },
              "&.Mui-focused fieldset": {
                borderColor: isDarkMode ? palette.gray[500] : palette.gray[500],
              },
            },
            "& input": {
              color: isDarkMode ? palette.gray.A100 : palette.gray[600],
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontFeatureSettings: "'liga' off, 'clig' off",
              fontFamily: "Roboto",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 400,
              letterSpacing: "0.4px",
              flex: "1 0 0",
            },
            "& input::placeholder": {
              color: isDarkMode ? palette.gray[400] : palette.neutral[500],
              opacity: 1,
            },
          }}
        />
        <IconButton icon={X} onClick={handleClose} ariaLabel="Cancel search" />
      </Box>
    </>
  );
};
